import { useLocation, useNavigate, Link } from 'react-router-dom'
import { Menu, LogOut, User, Settings, ChevronRight, Home, CalendarOff, CalendarCheck } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useAuthStore } from '@/stores/auth-store'
import { logout } from '@/services/auth/authService'
import { WorkspaceSwitcher } from './WorkspaceSwitcher'
import { buildBreadcrumbs, type BreadcrumbSegment } from '@/features/finance/utils/zones'
import { FinanceHeaderSearch } from '@/features/finance/components/global/FinanceHeaderSearch'
import { NotificationBell } from '@/features/notifications'
import { AwayChip, isMarkedAway, selfAwayMenuItem, useSetAway } from '@/features/away'

/**
 * Page title mapping based on route
 * Per App-Shell.md Section 2.3 - Header Component
 */
const pageTitles: Record<string, string> = {
  '/': 'Dashboard',
  '/quotations': 'Quotations',
  '/quotations/review': 'Quote Review',
  '/quotations/providers': 'Provider Selection',
  '/job-orders': 'Job Orders',
  '/customers': 'Customers',
  '/service-providers': 'Service Providers',
  '/carriers': 'Carriers',
  '/settings': 'Settings',
  // Finance pages
  '/finance/dashboard': 'Finance Dashboard',
  '/finance/transactions': 'Transactions',
  '/finance/allocations': 'Allocations',
  '/finance/receivables': 'Collections',
  '/finance/payables': 'Payments Due',
  '/finance/cheques': 'Cheque Tracking',
  '/finance/masters/accounts': 'Chart of Accounts',
  '/finance/masters/parties': 'Subledger Accounts',
  '/finance/reports': 'Reports',
  '/finance/settings': 'Finance Settings',
  // Finance reports
  '/finance/reports/balance-sheet': 'Balance Sheet',
  '/finance/reports/profit-loss': 'Profit & Loss',
  '/finance/reports/trial-balance': 'Trial Balance',
  '/finance/reports/general-ledger': 'General Ledger',
  '/finance/reports/cash-position': 'Cash Position',
  '/finance/reports/aging/receivables': 'Receivables Aging',
  '/finance/reports/aging/payables': 'Payables Aging',
  '/finance/reports/vat': 'VAT Report',
  // Finance settings
  '/finance/settings/voucher-types': 'Voucher Types',
  '/finance/settings/account-mapping': 'Account Mapping',
  '/finance/settings/fiscal-periods': 'Fiscal Periods',
  '/finance/settings/audit-trail': 'Audit Trail',
}

function getPageTitle(pathname: string): string {
  // Check for exact match first
  if (pageTitles[pathname]) {
    return pageTitles[pathname]
  }

  // Check for quotation detail
  if (pathname.startsWith('/quotations/') && pathname !== '/quotations/review' && pathname !== '/quotations/providers') {
    return 'Quotation Detail'
  }

  // Check for other detail pages
  if (pathname.startsWith('/customers/')) return 'Customer Detail'
  if (pathname.startsWith('/service-providers/')) return 'Provider Detail'
  if (pathname.startsWith('/carriers/')) return 'Carrier Detail'
  if (pathname.startsWith('/job-orders/')) return 'Job Order Detail'

  // Finance statement pages
  if (pathname.startsWith('/finance/reports/statement/customer/')) return 'Customer Statement'
  if (pathname.startsWith('/finance/reports/statement/vendor/')) return 'Vendor Statement'

  // Generic finance pages
  if (pathname.startsWith('/finance/')) return 'Finance'

  return 'FreighAI'
}

interface HeaderProps {
  className?: string
  onMenuClick?: () => void
  showMenuButton?: boolean
}

/**
 * Main header component
 * Per App-Shell.md Section 2.3
 *
 * Features:
 * - Page title (dynamic based on route)
 * - Search icon (placeholder)
 * - Notification bell with dot indicator
 * - User avatar dropdown
 */
export function Header({ className, onMenuClick, showMenuButton }: HeaderProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const user = useAuthStore((state) => state.user)
  const refreshMe = useAuthStore((state) => state.refreshPermissions)
  const pageTitle = getPageTitle(location.pathname)
  // "Mark me as away" / "I'm back" (desk-and-away-contract.md §6); null on an older server.
  const awayItem = selfAwayMenuItem(user)
  const { setAway, savingId: awaySaving } = useSetAway()

  // Finance module breadcrumbs
  const isFinanceModule = location.pathname.startsWith('/finance')
  const breadcrumbs = isFinanceModule ? buildBreadcrumbs(location.pathname) : []

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  // Get user initials for avatar
  const initials = user?.name
    ?.split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2) || 'U'

  return (
    <header
      className={cn(
        'flex h-14 items-center justify-between border-b bg-card px-4 lg:px-6',
        className
      )}
    >
      {/* Left side - Menu button (mobile) + Page title or Breadcrumbs */}
      <div className="flex items-center gap-3">
        {showMenuButton && (
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden h-9 w-9"
            onClick={onMenuClick}
            aria-label="Open menu"
          >
            <Menu className="h-5 w-5" />
          </Button>
        )}

        {/* Show breadcrumbs for Finance module, simple title otherwise */}
        {isFinanceModule ? (
          <FinanceBreadcrumbs breadcrumbs={breadcrumbs} />
        ) : (
          <h1 className="text-lg font-semibold text-foreground">{pageTitle}</h1>
        )}
      </div>

      {/* Right side - Search (Finance only) + Workspace switcher + User Menu */}
      <div className="flex items-center gap-3">
        {/* Finance Header Search - only show when inside Finance module (not on start page) */}
        {isFinanceModule && location.pathname !== '/finance' && (
          <FinanceHeaderSearch />
        )}

        {/* Workspace Switcher */}
        <WorkspaceSwitcher />

        {/* Notification bell + drawer */}
        <NotificationBell />

        {/* Marked away: said beside the avatar, so it is not forgotten while no tasks arrive. */}
        {isMarkedAway(user) && <AwayChip since={user?.awaySince} />}

        {/* User Menu. Opening it re-reads the signed-in person (`/auth/me`, quietly): a manager
            may have marked them away or back since they signed in. */}
        <DropdownMenu onOpenChange={(open) => { if (open) void refreshMe({ silent: true }) }}>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="relative h-9 w-9 rounded-full">
              <Avatar className="h-9 w-9">
                <AvatarFallback className="bg-primary text-primary-foreground text-sm">
                  {initials}
                </AvatarFallback>
              </Avatar>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>
              <div className="flex flex-col space-y-1">
                <p className="text-sm font-medium">{user?.name}</p>
                <p className="text-xs text-muted-foreground">{user?.email}</p>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link to="/settings" className="cursor-pointer">
                <User className="mr-2 h-4 w-4" />
                Profile
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/settings" className="cursor-pointer">
                <Settings className="mr-2 h-4 w-4" />
                Settings
              </Link>
            </DropdownMenuItem>
            {awayItem && user && (
              <DropdownMenuItem
                onClick={() => void setAway({ userId: user.userId, name: user.name }, awayItem.next)}
                disabled={awaySaving !== null}
                className="cursor-pointer"
              >
                {awayItem.next ? <CalendarOff className="mr-2 h-4 w-4" /> : <CalendarCheck className="mr-2 h-4 w-4" />}
                {awayItem.label}
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={handleLogout}
              className="cursor-pointer text-destructive focus:text-destructive"
            >
              <LogOut className="mr-2 h-4 w-4" />
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  )
}

/**
 * Breadcrumb navigation for Finance module
 */
function FinanceBreadcrumbs({ breadcrumbs }: { breadcrumbs: BreadcrumbSegment[] }) {
  if (breadcrumbs.length === 0) return null

  return (
    <nav className="flex items-center gap-1 text-sm" aria-label="Breadcrumb">
      {breadcrumbs.map((crumb, index) => {
        const isLast = index === breadcrumbs.length - 1
        const isFirst = index === 0

        return (
          <div key={crumb.href} className="flex items-center gap-1">
            {index > 0 && (
              <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
            )}
            {isLast ? (
              <span className="font-semibold text-foreground">{crumb.label}</span>
            ) : isFirst ? (
              // Finance Menu - prominent home button
              <Link
                to={crumb.href}
                className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-primary/10 text-primary font-medium hover:bg-primary/20 transition-colors"
              >
                <Home className="h-3.5 w-3.5" />
                <span>{crumb.label}</span>
              </Link>
            ) : (
              <Link
                to={crumb.href}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                {crumb.label}
              </Link>
            )}
          </div>
        )
      })}
    </nav>
  )
}
